/**
 * VeeValidateによるバリデーション定義
 */
/* eslint-disable @typescript-eslint/camelcase */
import { DateTime } from 'luxon';
import Vue from 'vue';
import { ValidationProvider, ValidationObserver, extend, localize } from 'vee-validate';
import ja from 'vee-validate/dist/locale/ja.json';
import { required, email, numeric, max, max_value, min_value, between, integer, alpha_num, confirmed } from 'vee-validate/dist/rules';

extend('required', required);
extend('email', {
  ...email,
  message: '有効なメールアドレスを入力してください'
});
extend('numeric', numeric);
extend('max', {
  ...max,
  message: '{length} 文字以内で入力してください'
});
extend('max_value', max_value);
extend('min_value', min_value);
extend('integer', integer);
extend('alpha_num', {
  ...alpha_num,
  message: '{_field_}は半角英数字のみ使用できます'
});
extend('confirmed', confirmed);

/* 納品処理モーダル内テーブルで使用する入力チェック */
extend("max_value_delivery_quantity", {
  ...max_value,
  message: "納品数の入力値に誤りがあります"
});
extend("min_value_delivery_quantity", {
  ...min_value,
  message: "納品数の入力値に誤りがあります"
});
extend("delivery_edit_quantity_required", {
  ...required,
  message: "納品修正数は入力必須です"
});
extend("between_delivery_edit_quantity", {
  ...between,
  message: "納品修正数は指定範囲内で入力ください"
});

/* 納品処理モーダル内テーブルで使用する入力チェック */
extend("check_delivery_location_id", {
  ...required,
  message: "納入場所が一致していないので選択してください"
});
/* 検収処理モーダル内テーブルで使用する入力チェック */
extend("max_value_acceptance_quantity", {
  ...max_value,
  message: "検収数の入力値に誤りがあります"
});
extend("min_value_acceptance_quantity", {
  ...min_value,
  message: "検収数の入力値に誤りがあります"
});
extend("between_acceptance_quantity", {
  ...between,
  message: "検収数は指定範囲内で入力ください"
});

extend('chk_after_the_day', {
  message: '当日以降の日付を入力してください',
  validate(value) {   
    const today = DateTime.fromISO(DateTime.local().toFormat('yyyy/MM/dd'));
    const inputDay = DateTime.fromISO(value);
    return inputDay >= today;
  }
});

extend('chk_min_date', {
  validate(value, params: any) {
    const targetDay = DateTime.fromISO(value); //入力値
    if (params.length > 0) {
      const minDay = DateTime.fromISO(DateTime.fromISO(String(params[0])).toFormat('yyyy/MM/dd'));
      return targetDay >= minDay ? true : `${params[1]}以降の日付を入力してください` ;
    } else {
      return true ;
    }
  }
});

extend('chk_max_date', {
  validate(value, params: any) {
    const targetDay = DateTime.fromISO(value); //入力値
    if (params.length > 0) {
      const maxDay = DateTime.fromISO(DateTime.fromISO(String(params[0])).toFormat('yyyy/MM/dd'));
      return targetDay <= maxDay ? true : `${params[1]}以前の日付を入力してください` ;
    } else {
      return true ;
    }
  }
});

extend('selectRequired', {
  message: '{_field_}は必須項目です',
  ...required,
  validate(value) {
    const val = typeof value === 'object' ? value.value : value;
    if (val) {
      return true;
    }
    return false;
  }
});

extend('multiSelectRequired', {
  message: '{_field_}は必須項目です',
  ...required,
  validate(value) {
    return Array.isArray(value) && value.length > 0 ? true : false ;
  }
});

extend('integer_greater_than_zero', {
  message: '{_field_}は0より大きい整数で入力してください',
  ...required,
  validate(value) {
    const val = Number(value);
    if (val > 0 && Number.isInteger(val)) {
      return true;
    }
    return false;
  }
});

/**
 * アップロードファイルサイズチェック
 * 初期値はtrueを返却
 */
extend('fileMaxSize', {
  ...required,
  validate(value: File | File[], params: any) {

    const maxmum = Number(Object.values(params)[0]);
    if (!Number.isInteger(maxmum)) return true;

    const errMessage = 'ファイルサイズを確認してください ';

    const checkSize = (name: string, size: number, max: number): string | null => {
      return size >= maxmum ? name : null;
    };

    if (Array.isArray(value)){
      const ngFileNames: string[] = [];
      for (const i in value) {
        const check = checkSize(value[i].name, value[i].size, maxmum);
        if (check !== null) ngFileNames.push(check);
      }
      return ngFileNames.length === 0 ? true : `${errMessage}(${ngFileNames.join('、')})` ;
    }

    const check = checkSize(value.name, value.size, maxmum);
    return check === null ? true : `${errMessage}(${check})` ;

  }
});

/**
 * アップロードファイル数チェック
 * 初期値はtrueを返却
 * paramsには現状アップ数を動的差し引いた数を付与
 */
 extend('fileMaxCount', {
  ...required,
  validate(value: File | File[], params: any) {

    const upLoadLimit = Number(Object.values(params)[0]);
    if (!Number.isInteger(upLoadLimit)) return true;

    const errMessage = `アップロード可能ファイル数の上限を超えています(${upLoadLimit}まで選択可能)` ;

    const upLoadFileList: File[] = Array.isArray(value) ? [...value] : [value] ;
    return upLoadFileList.length > upLoadLimit ? errMessage : true ;

  }
});

/**
 * アップロードファイルの拡張子チェック
 * 初期値はtrueを返却
 */
extend('fileAllowExt', {
  ...required,
  validate(value: File | File[], params: any) {
    if (Object.values(params).length === 0) {
      return true;
    }

    const errMessage = '対応しないファイルが選択されています。ファイル拡張子を確認してください。';
    const allowedExts = Object.values(params)[0] as string[];

    const checkExt = (name: string, allowList: string[]) => {
      const ext = name.split('.');
      if (ext[1] === undefined || ext[1].length === 0) return name;
      const check = allowList.some(value => ext[1] === value);
      return check ? null : name;
    };

    if (Array.isArray(value)) {
      const ngFileNames: string[] = [];
      for (const i in value) {
        const check = checkExt(value[i].name, allowedExts);
        if (check !== null) ngFileNames.push(check);
      }
      return ngFileNames.length === 0 ? true : `${errMessage}(${ngFileNames.join('、')})`;
    }

    const getVaild = checkExt(value.name, allowedExts);
    return getVaild === null ? true : `${errMessage}(${getVaild})`;
  }
});

/**
 * アップロードファイルの拡張子チェック
 * 初期値はtrueを返却
 */
 extend('fileAllowType', {
  ...required,
  validate(value: File | File[], params: any) {

    if (Object.values(params).length === 0) {
      return true;
    }

    const errMessage = 'ファイルの種類を確認してください ';
    const allowedExts = Object.values(params)[0] as string[];

    const checkType = (name: string, type: string, allowList: string[]) => {
      const check = allowList.every(value => type.indexOf(value) === -1);
      return check ? name : null;
    };

    if (Array.isArray(value)){
      const ngFileNames: string[] = [];
      for (const i in value) {
        const check = checkType(value[i].name, value[i].type, allowedExts);
        if (check !== null) ngFileNames.push(check);
      }
      return ngFileNames.length === 0 ? true : `${errMessage}(${ngFileNames.join('、')})` ;
    }

    const getVaild = checkType(value.name, value.type, allowedExts);
    return getVaild === null ? true : `${errMessage}(${getVaild})` ;

  }
});

/**
 * アップロードファイル数チェック
 * 初期値はtrueを返却
 */
 extend('fileRequired', {
  ...required,
  validate(value: any, params: any) {
    const errMessage = 'ファイルを添付してください';
    const fileCount = value;
    if (!Number.isInteger(fileCount)) return true;
    return fileCount === 0 ? errMessage : true;
  }
});

/**
 * 書式チェック電話番号他
 * 9999-9999-9999形式のデータのみ許可
 **/
extend('wrong_format_for_number_and_hyphen', {
  message: '{_field_}は半角数字9999-9999-9999形式のみ許可されています',
  ...required,
  validate(value) {
    // 空も許可する
    if (value === undefined || value === null || value === '') {
      return true;
    }
    const val = /^[0-9]+-[0-9]+-[0-9]+$/.test(value);
    if (val) {
      return true;
    }
    return false;
  }
});

/**
 * 書式チェック郵便番号の正規表現チェック
 * 123-456形式のデータのみ許可（Docurain連携のため）
 **/
extend('wrong_format_for_postal_code', {
  message: '{_field_}は半角数字123-4567形式のみ許可されています',
  ...required,
  validate(value) {
    // 空も許可する
    if (value === undefined || value === null || value === '') {
      return true;
    }
    const val = /^[0-9]{3}-[0-9]{4}$/.test(value);
    if (val) {
      return true;
    }
    return false;
  }
});

/**
 * 全角カタカナの正規表現チェック
 * 全角カタカナもしくは全角スペースのみ許可（コラボワンに合わせる）
 **/
extend('fullwidth_katakana_and_space_only', {
  message: '{_field_}は全角のカタカナかスペースのみ許可されています',
  ...required,
  validate(value) {
    // 空も許可する
    if (value === undefined || value === null || value === '') {
      return true;
    }
    // u30A0：゠、u30FF：ヿ、\u3000：全角スペース
    const val = /^[\u30A0-\u30FF\u3000]+$/.test(value);
    if (val) {
      return true;
    }
    return false;
  }
});

/**
 * 全角文字の正規表現チェック
 * 全角文字のみ許可（コラボワンに合わせる）
 **/
extend('fullwidth_only', {
  message: '{_field_}は全角のみ許可されています',
  ...required,
  validate(value) {
    // 空も許可する
    if (value === undefined || value === null || value === '') {
      return true;
    }
    // 文字コードの場合 const val = /^[^\x20-\x7E\uFF61-\uFF9F]+$/.test(value);
    const val = /^[^ -~｡-ﾟ]+$/.test(value);
    if (val) {
      return true;
    }
    return false;
  }
});

extend("between_quantity", {
  ...between,
  message: "指定範囲内で入力ください"
});
extend("max_value_quantity", {
  ...max_value,
  message: "入力値に誤りがあります"
});
extend("min_value_quantity", {
  ...min_value,
  message: "入力値に誤りがあります"
});
extend("max_limit_quantity", {
  ...max_value,
  validate(value: any, params: any) {
    return `${params.max}以下の値を入力してください`;
  }
});
extend("min_limit_quantity", {
  ...min_value,
  validate(value: any, params: any) {
    return `${params.min}以上の値を入力してください`;
  }
});

extend("between_price", {
  ...between,
  message: "指定範囲内で入力ください"
});
extend("max_value_price", {
  ...max_value,
  message: "入力値に誤りがあります"
});
extend("min_value_price", {
  ...min_value,
  message: "入力値に誤りがあります"
});
extend("max_limit_price", {
  ...max_value,
  validate(value: any, params: any) {
    return `${params.max}以下の値を入力してください`;
  }
});
extend("min_limit_price", {
  ...min_value,
  validate(value: any, params: any) {
    return `${params.min}以上の値を入力してください`;
  }
});

extend('greater_than_or_equal_to_zero', {
  message: '{_field_}は0以上の整数で入力してください',
  ...required,
  validate(value) {
    const val = Number(value);
    if (val >= 0 && Number.isInteger(val)) {
      return true;
    }
    return false;
  }
});

extend('table_input_item_required', {
  ...required,
  validate(value) {
    const val = typeof value === 'object' ? value.value : value;
    if (val) {
      return true;
    }
    return false;
  },
  message: '必須入力項目です'
});

extend('estimate_rate', {
  ...required,
  validate(value) {
    // 空も許可する
    if (value === undefined || value === null || value === '') {
      return true;
    }
    const val = /^[0-9]+(\.[0-9][1-9]?)?$/.test(value);
    if (val) {
      return true;
    }
    return false;
  },
  message: '小数点以下2桁まで入力可能です。'
});

extend('error_postal_code', {
  message: '取得に失敗しました。存在しない郵便番号の可能性があります',
  ...required,
  validate(value: any, params: any) {
    if (params.allowFalse === 'false') {
      return true;
    }
    return false;
  }
});

/**
 * 刻み値チェック
 */
extend('quantity_step_value', {
  message: '刻み値エラー',
  ...required,
  validate(value: any, params: any) {
    if (params.allowFalse === 'false') {
      return true;
    }
    return false;
  }
});

extend('send_tab_input_item_required', {
  ...required,
  message: '必須入力項目です'
});

extend('corporate_number_lengthCheck', {
  validate(value: any, params: any) {
    if (value.length === Number(params[0])) {
      return true;
    } else {
      return '{_field_}は数値13文字で入力してください';
    }
  }
});

extend('agency_id_lengthCheck', {
  validate(value: any, params: any) {
    if (value.length === Number(params[0])) {
      return true;
    } else {
      return '{_field_}は半角英数字9文字で入力してください';
    }
  }
});

localize('ja', ja);

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
